// src/components/Dashboard/Cards/ChartsCard.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DashboardCard from './DashboardCard';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const ChartsCard = ({ onHide }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Credits distribution data
  const [creditsData, setCreditsData] = useState([]);
  const [totalCreditsStudents, setTotalCreditsStudents] = useState(0);

  // Plan distribution data
  const [planData, setPlanData] = useState([]);
  const [totalPlanStudents, setTotalPlanStudents] = useState(0);

  // Example weekly change states (placeholder values)
  const [weeklyImprovement, setWeeklyImprovement] = useState(10);
  const [weeklyDecline, setWeeklyDecline] = useState(5);
  const [averageCredits, setAverageCredits] = useState(9);
  const [newPlansCreated, setNewPlansCreated] = useState(22);

  // Toggle to switch between real and fake data
  const USE_FAKE_DATA = true; // set to false to fetch real data

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (USE_FAKE_DATA) {
          const totalStudents = 367;

          // Fake data for credits (5 slices) using your color palette
          setCreditsData([
            { name: 'No Credits', value: 51, color: '#e11d48' },
            { name: '1 - 6 Credits', value: 99, color: '#fb923c' },
            { name: '7 - 9 Credits', value: 82, color: '#facc15' },
            { name: '10 - 12 Credits', value: 88, color: '#4ade80' },
            { name: '13+ Credits', value: 47, color: '#38bdf8' },
          ]);
          setTotalCreditsStudents(totalStudents);

          // Fake data for plan (2 slices)
          setPlanData([
            { name: 'Students with a plan', value: 200, color: '#38bdf8' },
            { name: 'Students without a plan', value: 167, color: '#fb923c' },
          ]);
          setTotalPlanStudents(totalStudents);
        } else {
          // Real data logic here
          // Example placeholder:
          setCreditsData([
            { name: 'No Credits', value: 1, color: '#e11d48' },
            { name: '1 - 6 Credits', value: 0, color: '#fb923c' },
            { name: '7 - 9 Credits', value: 0, color: '#facc15' },
            { name: '10 - 12 Credits', value: 5, color: '#4ade80' },
            { name: '13+ Credits', value: 0, color: '#38bdf8' },
          ]);
          setTotalCreditsStudents(6);

          setPlanData([
            { name: 'Students with a plan', value: 10, color: '#38bdf8' },
            { name: 'Students without a plan', value: 4, color: '#fb923c' },
          ]);
          setTotalPlanStudents(14);
        }

        // You could also fetch or calculate weekly changes, average credits, etc.
        setIsLoading(false);
      } catch (err) {
        console.error('[ChartsCard] Error:', err);
        toast.error('Failed to load chart data.');
        setIsLoading(false);
      }
    };

    fetchData();
  }, [USE_FAKE_DATA]);

  /**
   * CHART CONFIGS
   * Minimizes whitespace with maintainAspectRatio = false and a smaller cutout if desired.
   */
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%', // thickness of the donut ring
    plugins: {
      legend: {
        display: false, // We’ll render our own legend
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const value = ctx.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  // Construct Chart.js-compatible data for Credits
  const creditsChartData = {
    labels: creditsData.map((d) => d.name),
    datasets: [
      {
        data: creditsData.map((d) => d.value),
        backgroundColor: creditsData.map((d) => d.color),
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  // Construct Chart.js-compatible data for Plan
  const planChartData = {
    labels: planData.map((d) => d.name),
    datasets: [
      {
        data: planData.map((d) => d.value),
        backgroundColor: planData.map((d) => d.color),
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  return (
    <DashboardCard
      title={<span className="font-medium">Scheduling Insights</span>}
      onHide={onHide}
      isLoading={isLoading}
    >
      {!isLoading && (
        <div className="flex flex-col gap-3">
          {/* Main row: 2 donut charts + mini-cards on the right */}
          <div className="flex flex-col lg:flex-row items-stretch justify-between gap-3">
            {/* LEFT SECTION: Two Donut Charts */}
            <div className="flex-1 flex flex-col md:flex-row gap-1">  
              {/* CREDITS CHART */}
              <div className="flex flex-col bg-white border border-gray-200 rounded p-2 w-full md:w-1/2">
                <div className="flex flex-col sm:flex-row items-start gap-2 mt-2">
                  {/* Donut Chart */}
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 overflow-visible">
                    <Doughnut data={creditsChartData} options={commonOptions} />
                    {/* Center label => total students */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-sm font-semibold">
                        {totalCreditsStudents}
                      </span>
                      <span className="text-xs text-gray-600">Students</span>
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="min-w-[12.5vw] text-xs sm:text-sm">
                    <div className="font-semibold mb-1">Credits Distribution | Fall 2025 - Spring</div>
                    <div className="space-y-1">
                      {creditsData.map((item) => (
                        <div
                          key={item.name}
                          className="flex justify-between items-center"
                        >
                          <div className="flex items-center">
                            <span
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: item.color }}
                            />
                            <span>{item.name}</span>
                          </div>
                          <span>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* PLAN CHART */}
              <div className="flex flex-col bg-white border border-gray-200 rounded p-2 w-full md:w-1/2">
                <div className="flex flex-col sm:flex-row items-start gap-2 mt-2">
                  {/* Donut Chart */}
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 overflow-visible">
                    <Doughnut data={planChartData} options={commonOptions} />
                    {/* Center label => total students */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-sm font-semibold">
                        {totalPlanStudents}
                      </span>
                      <span className="text-xs text-gray-600">Students</span>
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="min-w-[14.5vw] text-xs sm:text-sm">
                    <div className="font-semibold mb-1">
                      Plan Creation Distribution | Fall 2025 - Spring</div>
                    <div className="space-y-1">
                      {planData.map((item) => (
                        <div
                          key={item.name}
                          className="flex justify-between items-center"
                        >
                          <div className="flex items-center">
                            <span
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: item.color }}
                            />
                            <span>{item.name}</span>
                          </div>
                          <span>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SECTION: 4 MINI-CARDS (Weekly changes, etc.) */}
            <div className="flex flex-col gap-2 w-full lg:w-60 text-xs sm:text-sm">
              <div className="flex items-center justify-between p-2 border rounded">
                <span className="font-semibold">New Plans This Week</span>
                <div className="flex items-center space-x-1 text-green-600">
                  <span className="font-medium">{weeklyImprovement}</span>
                  <span>Students</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-2 border rounded">
                <span className="font-semibold">Average Credits</span>
                <span className="font-medium text-gray-800">
                  {averageCredits}
                </span>
              </div>

              <div className="flex items-center justify-between p-2 border rounded">
                <span className="font-semibold">New Plans Created</span>
                <span className="font-medium text-blue-600">
                  {newPlansCreated}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export default ChartsCard;
